<template>
  <div class="testgen">
    <h1 class="testgen__title">Test generare prompturi (doar poze)</h1>

    <div class="testgen__controls glass-card">
      <label class="testgen__field">
        <span>Selfie</span>
        <input type="file" accept="image/*" @change="onFileChange" />
      </label>

      <img v-if="selfiePreview" :src="selfiePreview" class="testgen__preview" />

      <label class="testgen__field">
        <span>Gen</span>
        <select v-model="gender">
          <option value="male">male</option>
          <option value="female">female</option>
        </select>
      </label>

      <div class="testgen__field">
        <span>Persona</span>
        <div class="testgen__persona-checks">
          <label v-for="persona in personaList" :key="persona.id" class="testgen__persona-check">
            <input type="checkbox" v-model="selectedPersonas" :value="persona.id" />
            <span :style="{ color: persona.color }">{{ persona.name }}</span>
          </label>
        </div>
      </div>

      <button class="neon-btn" :disabled="!selfiePreview || running || selectedPersonas.length === 0" @click="start">
        {{ running ? 'SE GENEREAZĂ...' : 'START' }}
      </button>
    </div>

    <div class="testgen__grid">
      <div
        v-for="card in cards"
        :key="card.key"
        class="testgen__card glass-card"
        :style="{ '--p-color': card.persona.color }"
      >
        <p class="testgen__card-title">
          {{ card.persona.name }} <span class="testgen__variant">#{{ card.variant }}</span>
        </p>

        <div class="testgen__image-wrap">
          <img v-if="card.status === 'done'" :src="card.url" class="testgen__image" />
          <div v-else-if="card.status === 'error'" class="testgen__error">{{ card.error }}</div>
          <div v-else class="testgen__spinner"><div class="spinner-ring"></div></div>
        </div>

        <details class="testgen__prompt" v-if="card.prompt">
          <summary>Prompt folosit</summary>
          <p>{{ card.prompt }}</p>
        </details>

        <div v-if="card.status === 'done'" class="testgen__video-section">
          <button
            v-if="card.videoStatus !== 'pending' && card.videoStatus !== 'done'"
            class="neon-btn testgen__video-btn"
            @click="startVideo(card)"
          >
            {{ card.videoStatus === 'error' ? 'REÎNCEARCĂ VIDEO' : 'GENERATE VIDEO' }}
          </button>

          <div v-if="card.videoStatus === 'pending'" class="testgen__video-progress">
            <div class="testgen__progress-track">
              <div class="testgen__progress-fill" :style="{ width: Math.round(card.videoProgress * 100) + '%' }"></div>
            </div>
            <p class="testgen__video-status">SE GENEREAZĂ VIDEO... {{ Math.round(card.videoProgress * 100) }}%</p>
          </div>

          <p v-if="card.videoStatus === 'error'" class="testgen__error">{{ card.videoError }}</p>

          <video
            v-if="card.videoStatus === 'done'"
            :src="card.videoUrl"
            class="testgen__video"
            controls
            autoplay
            loop
            muted
            playsinline
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { PERSONAS } from '@/data/personas'
import { generateImage, generateVideo, listPhotoVariants } from '@/services/backend'

const personaList = Object.values(PERSONAS)

const selfiePreview = ref(null)
const gender = ref('male')
const running = ref(false)
const cards = ref([])
const selectedPersonas = ref(personaList.map((p) => p.id))

function onFileChange(e) {
  const file = e.target.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => { selfiePreview.value = reader.result }
  reader.readAsDataURL(file)
}

async function start() {
  running.value = true
  cards.value = []

  const chosenPersonas = personaList.filter((p) => selectedPersonas.value.includes(p.id))

  for (const persona of chosenPersonas) {
    let variants = []
    try {
      variants = await listPhotoVariants(persona.id, gender.value)
    } catch {
      variants = []
    }

    if (variants.length === 0) {
      cards.value.push({
        key: `${persona.id}-none`,
        persona,
        variant: '-',
        status: 'error',
        error: `Niciun prompt "photo" completat pentru ${persona.id}/${gender.value}.`,
        url: null,
        prompt: null,
      })
      continue
    }

    for (const variant of variants) {
      const card = makeCard(persona, variant)
      cards.value.push(card)
      runOne(card, persona, variant)
    }
  }

  running.value = false
}

function makeCard(persona, variant) {
  // reactive() explicit — cardul e mutat direct mai târziu în runOne(), nu prin
  // array-ul cards.value; fără asta Vue nu observă schimbările și UI-ul rămâne blocat.
  return reactive({
    key: `${persona.id}-${variant}`,
    persona,
    variant,
    status: 'pending',
    url: null,
    prompt: null,
    error: null,
    videoStatus: 'idle',
    videoUrl: null,
    videoProgress: 0,
    videoError: null,
  })
}

async function runOne(card, persona, variant) {
  try {
    const result = await generateImage(
      selfiePreview.value,
      { personaId: persona.id, gender: gender.value, variant },
      () => {}
    )
    card.status = 'done'
    card.url = result.url
    card.prompt = result.prompt
  } catch (e) {
    card.status = 'error'
    card.error = e.message || 'Eroare necunoscută.'
  }
}

async function startVideo(card) {
  card.videoStatus = 'pending'
  card.videoProgress = 0
  card.videoError = null

  try {
    const result = await generateVideo(
      card.url,
      { personaId: card.persona.id, gender: gender.value, variant: card.variant },
      (p) => { card.videoProgress = p }
    )
    card.videoStatus = 'done'
    card.videoUrl = result.url
  } catch (e) {
    card.videoStatus = 'error'
    card.videoError = e.message || 'Eroare necunoscută.'
  }
}
</script>

<style scoped>
.testgen {
  /* App.vue pune .app-shell pe position:fixed + overflow:hidden (gândit pentru
     ecranele kiosk-ului, care încap mereu exact în 100vh) — pagina asta are nevoie
     de scroll propriu, deci își creează propriul context de scroll local. */
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 2rem;
  background: #0b0b12;
  color: #fff;
  font-family: 'Rajdhani', sans-serif;
}

.testgen__title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

.testgen__controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.testgen__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.testgen__preview {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
}

.testgen__persona-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 1rem;
}

.testgen__persona-check {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.testgen__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 1.8rem;
}

.testgen__card {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  border-left: 4px solid var(--p-color, #7c3aed);
}

.testgen__card-title {
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--p-color, #fff);
}

.testgen__variant {
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
}

.testgen__image-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
}

.testgen__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.testgen__error {
  color: rgba(255, 100, 100, 0.85);
  font-size: 0.8rem;
  padding: 1rem;
  text-align: center;
}

.spinner-ring {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(139, 92, 246, 0.2);
  border-top-color: rgba(139, 92, 246, 0.8);
  animation: spin 1s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.testgen__prompt {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.55);
}

.testgen__prompt summary {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.75);
}

.testgen__prompt p {
  margin-top: 0.4rem;
  line-height: 1.4;
}

.testgen__video-section {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.testgen__video-btn {
  padding: 0.7rem 1.5rem;
  font-size: 0.85rem;
  align-self: flex-start;
}

.testgen__video-progress {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.testgen__progress-track {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: hidden;
}

.testgen__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--p-color, #7c3aed), #60a5fa);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.testgen__video-status {
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.4);
}

.testgen__video {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  background: #000;
  object-fit: cover;
}
</style>
